const home = {
    eyebrow: 'Oracle Consults',
    title: 'I build backend systems that make operations reliable.',
    intro:
      'Founder-led consulting for inventory, order, reservation, reporting, integration, and internal workflow software that businesses actually depend on.',
    heroSupport:
      'Former Backend Engineer at Oponeo.pl with 2.5+ years of production .NET delivery, a serious systems portfolio, and a mechatronics-shaped habit of thinking in constraints, reliability, and failure modes.',
    focusAreas: [
      'Backend systems in .NET and ASP.NET Core',
      'SQL performance and data flow optimization',
      'Python automation, APIs, and diagnostics',
      'Clear communication for technical and business stakeholders'
    ],
    ctaLabel: 'Book a 15-min discovery call',
    secondaryCtaLabel: 'See case studies',
    bookingHint:
      'The call is short, practical, and low-pressure: we map the operational problem, the constraints around it, and the best next step.',
    sectionLabels: {
      credibility: 'Credibility',
      problemsSolved: 'Problems Solved',
      story: 'Story',
      packages: 'Packages',
      proof: 'Proof',
      metrics: 'Metrics',
      communication: 'Communication',
      resources: 'Resources',
      contact: 'Contact',
      bestFor: 'Best for',
      outcome: 'Outcome'
    },
    trustTitle: 'Operational software, not generic freelancing',
    trustSummary:
      'The strongest story here is practical and specific: real production work, real business-system projects, and a consulting offer shaped around workflows that cost time and money when they break.',
    trustPoints: [
      'Commercial backend work in a high-traffic e-commerce environment at Oponeo.pl',
      'StockIT validated with feedback from more than 10 small business owners',
      'Repeated evidence of about 25% SQL performance improvement in production work',
      'Multilingual communication and tutoring experience that strengthens delivery clarity'
    ],
    proofBar: [
      { value: 'Oponeo.pl', label: 'former backend engineer in production e-commerce' },
      { value: '~25%', label: 'SQL performance improvement repeated across commercial evidence' },
      { value: '10+ owners', label: 'validated StockIT inventory workflows' },
      { value: '4.26/5.0', label: 'B.Eng. Computer Engineering & Mechatronics' },
      { value: '5 languages', label: 'English, French, Polish, Spanish, Kinyarwanda' }
    ],
    problemTitle: 'Problems I help solve',
    problemIntro:
      'Clients do not hire backend work for its own sake. They hire it because inventory is wrong, reservations are brittle, integrations fail, or the system slows down when the business starts depending on it.',
    problems: [
      {
        title: 'Inventory and stock visibility',
        description:
          'Backends and internal tools for product, stock, reporting, and export workflows where accuracy and visibility matter daily.'
      },
      {
        title: 'Reservations and multi-role workflows',
        description:
          'Systems where customers, admins, vendors, or operators all interact with one source of truth and the state transitions cannot be hand-waved.'
      },
      {
        title: 'Integrations and legacy friction',
        description:
          'Payment, logistics, and API integrations where reliability, data shape, and maintainability matter more than just getting a happy-path demo working.'
      },
      {
        title: 'Automation and internal tooling',
        description:
          'Python services, scripts, and diagnostics that remove repetitive work, improve operational visibility, and support sharper decision-making.'
      }
    ],
    storyTitle: 'Why I build business systems',
    storyCards: [
      {
        title: 'Personal motivation',
        text:
          'I grew up around small-business operations and saw directly how inventory gaps, manual workflows, and poor visibility create daily friction for owners and operators.'
      },
      {
        title: 'StockIT as proof',
        text:
          'That is why StockIT matters. It was not a generic student project. It was a serious attempt to solve an operational problem through structured software and user validation.'
      },
      {
        title: 'Mechatronics mindset',
        text:
          'My background in computer engineering and mechatronics makes me think in systems: boundaries, dependencies, state, diagnostics, and long-term reliability.'
      }
    ],
    packageTitle: 'Ways to start working together',
    packageIntro:
      'To reduce project uncertainty, the first step does not need to be a large build. These are clear, lower-friction ways to start.',
    packages: [
      {
        title: 'System Health Audit',
        format: 'One-time review',
        idealFor: 'Teams with a backend that feels slow, brittle, or hard to extend.',
        deliverables: [
          'Review of architecture, data flow, integration pressure points, and bottlenecks',
          'Prioritized list of the biggest technical risks and quickest wins',
          'Written guidance you can use internally or continue with me'
        ],
        outcome: 'Clarity before more time or money is spent in the wrong place.'
      },
      {
        title: 'Integration and Performance Sprint',
        format: 'Short focused engagement',
        idealFor: 'Businesses dealing with slow queries, fragile integrations, or backend workflows that keep causing operational pain.',
        deliverables: [
          'Targeted SQL and backend flow improvements',
          'Refactoring around the most brittle integration or workflow area',
          'Concrete before-and-after documentation for the changed area'
        ],
        outcome: 'A smaller scope with visible technical and operational improvement.'
      },
      {
        title: 'Fractional Backend Architect',
        format: 'Ongoing advisory or delivery support',
        idealFor: 'Founders and teams that need a senior backend partner without hiring full-time yet.',
        deliverables: [
          'Architecture guidance and implementation direction',
          'Review of priorities, risks, and system evolution choices',
          'Communication support across technical and non-technical stakeholders'
        ],
        outcome: 'Stronger backend decisions, less rework, and more engineering confidence.'
      }
    ],
    featuredTitle: 'Selected proof',
    featuredIntro:
      'The quickest way to understand the offer is to look at the kind of work already behind it.',
    featuredProof: [
      {
        title: 'Commercial anchor: Oponeo.pl',
        summary:
          '2.5+ years of .NET backend work in a high-traffic e-commerce environment involving APIs, SQL tuning, integrations, legacy refactoring, testing, and production support.',
        link: '/projects',
        linkLabel: 'See commercial proof'
      },
      {
        title: 'StockIT inventory platform',
        summary:
          'A thesis-driven inventory system shaped by real small-business pain and validated with feedback from more than 10 small business owners.',
        link: '/projects',
        linkLabel: 'Read the StockIT case study'
      },
      {
        title: 'Reservation and platform workflows',
        summary:
          'Work across DreamNDine and the EStore platform shows comfort with role separation, domain logic, reservation state, and backend systems that coordinate real operations.',
        link: '/projects',
        linkLabel: 'Browse case studies'
      }
    ],
    metricsTitle: 'Metrics I care about',
    metrics: [
      {
        title: 'Query performance',
        description:
          'Repeated commercial evidence points to about 25% SQL performance improvement through tuning, indexing, and better data-access structure.'
      },
      {
        title: 'Workflow accuracy',
        description:
          'Inventory, reservation, and reporting systems only matter if operators can trust what the system says and what state it is in.'
      },
      {
        title: 'Integration reliability',
        description:
          'Payments, logistics, and third-party APIs are where brittle systems often fail. I focus on the boundaries and failure handling, not only the happy path.'
      },
      {
        title: 'Operational time saved',
        description:
          'A good backend reduces repetitive manual work, improves visibility, and gives teams less chaos to manage every day.'
      }
    ],
    communicationTitle: 'Communication and delivery style',
    communication:
      'I do not only build software. I teach programming and languages as well, which strengthened my ability to explain complex systems clearly, document decisions, and adjust communication for different technical levels and cultures.',
    resourcesTitle: 'Useful next steps for cautious buyers',
    resourcesIntro:
      'Not everyone is ready to start with a project immediately. These resources help you evaluate fit, understand the work more clearly, and start with lower-friction material first.',
    resources: [
      {
        kicker: 'Insights',
        title: 'Read backend notes and engineering posts',
        summary:
          'Browse practical notes on backend performance, integrations, automation, diagnostics, and operational software design.',
        link: '/blog',
        linkLabel: 'Open insights',
        external: false
      },
      {
        kicker: 'Checklist',
        title: 'Download the backend health audit checklist',
        summary:
          'A concise checklist for spotting brittle workflows, slow data paths, risky integrations, and architecture issues before they get more expensive.',
        link: '/doc/backend-health-audit-checklist.md',
        linkLabel: 'Download checklist',
        external: true,
        download: true
      },
      {
        kicker: 'Engagement fit',
        title: 'See the service formats and best-fit projects',
        summary:
          'Review the package structure, the kinds of teams I help most, and how an audit or focused sprint usually starts.',
        link: '/expertise',
        linkLabel: 'Review services',
        external: false
      }
    ],
    contactTitle: 'Tell me what is slow, brittle, or hard to trust',
    contactText:
      'Good fit projects include workflow-heavy backends, internal tools, inventory or reservation systems, integrations, SQL tuning, and architecture reviews for product teams that need cleaner backend direction.',
    contactPrimaryLabel: 'Contact me',
    contactSecondaryLabel: 'Schedule a discovery call'
  };

export default home;
